import { Link } from '@tanstack/react-router';
import { ArrowRight, NotebookPen, PauseCircle, XCircle } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { DECISION_NOTE_MAX, type DebriefView, type StageDecisionOutcome } from '@nest-recruit/shared';
import { Button, type ButtonVariant } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER } from '../../pipeline/move-intent';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import styles from './DecisionBar.module.css';

export interface DecisionChoice {
  readonly outcome: StageDecisionOutcome;
  readonly reasonCode?: string;
  readonly note: string | null;
  readonly poolIds: readonly string[];
  readonly newPoolNames: readonly string[];
}

/**
 * F12.03–F12.07 — Advance, Hold or Reject. ADR 0077 (C5): a round has no rule or pass mark, so there is
 * nothing to override — the decider reads the weighted result and calls it. Reject asks its §L19 reason
 * in the same picker the pipeline uses (#2), and pool-eligible reasons offer a pool.
 */
export function DecisionBar({ view, pending, onRecord }: { readonly view: DebriefView; readonly pending: StageDecisionOutcome | null; readonly onRecord: (choice: DecisionChoice) => void }): ReactNode {
  const [note, setNote] = useState('');
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [reasonsAt, setReasonsAt] = useState<AnchorRect | null>(null);
  const options = usePipelineOptions(view.can.pool);
  const outcome = view.outcome;

  if (!outcome || !view.can.decide) {
    return (
      <Card title="Decision">
        <p className={styles.blocked}>{view.can.blockedReason ?? 'Nothing can be recorded here.'}</p>
        {outcome?.verdict === 'blocked' && (
          <Link to="/interviews/$interviewId/scorecards" params={{ interviewId: view.interviewId }} className={styles.link}>
            Open the scorecards
          </Link>
        )}
      </Card>
    );
  }

  const trimmedNote = note.trim() === '' ? null : note.trim();

  function choose(chosen: StageDecisionOutcome, anchor: AnchorRect): void {
    if (chosen === 'reject') return setReasonsAt(anchor);
    onRecord({ outcome: chosen, note: trimmedNote, poolIds: [], newPoolNames: [] });
  }

  const advanceLabel = view.round.nextRoundName ? `Advance to ${view.round.nextRoundName}` : 'Pass — on to final selection';
  const buttons: readonly { readonly outcome: StageDecisionOutcome; readonly label: string; readonly icon: typeof ArrowRight }[] = [
    { outcome: 'advance', label: advanceLabel, icon: ArrowRight },
    { outcome: 'hold', label: 'Hold', icon: PauseCircle },
    { outcome: 'reject', label: 'Reject', icon: XCircle },
  ];

  return (
    <>
      <Card title="Your decision">
        <div className={styles.body}>
          <p className={styles.consequence}>{consequence(view)}</p>
          {isNoteOpen ? (
            <Field label="Note for the record" hint="Optional. Everyone who reads the decision log sees it.">
              {({ inputId, describedBy }) => (
                <Textarea id={inputId} aria-describedby={describedBy} rows={2} value={note} maxLength={DECISION_NOTE_MAX} placeholder="e.g. Probe scale of operations in round 2" autoFocus onChange={(event) => setNote(event.target.value)} />
              )}
            </Field>
          ) : (
            <Button variant="ghost" size="sm" icon={NotebookPen} onClick={() => setIsNoteOpen(true)}>
              Add a note for the record
            </Button>
          )}
        </div>
      </Card>
      {/* Only the buttons stick to the bottom, as the cockpit's submit bar does — the decider never scrolls to find them. */}
      <div className={styles.bar} role="group" aria-label="Record the decision">
        {buttons.map((button) => (
          <Button
            key={button.outcome}
            variant={variantFor(button.outcome)}
            icon={button.icon}
            disabled={pending !== null}
            isPending={pending === button.outcome}
            onClick={(event: MouseEvent<HTMLButtonElement>) => choose(button.outcome, anchorFrom(event.currentTarget))}
          >
            {button.label}
          </Button>
        ))}
      </div>

      <Popover anchor={reasonsAt} onClose={() => setReasonsAt(null)} align="right" label={`Reject ${view.candidateName}`}>
        {reasonsAt && (
          <ReasonPicker
            mode="reject"
            reasons={view.rejectionReasons.map((reason) => ({ ...reason, minimumContactAttempts: null }))}
            pools={options.data?.pools ?? []}
            canPool={view.can.pool}
            contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
            candidateName={view.candidateName}
            onConfirm={(choice) => {
              setReasonsAt(null);
              onRecord({ outcome: 'reject', reasonCode: choice.reasonCode, note: trimmedNote, poolIds: choice.poolIds, newPoolNames: choice.newPoolNames });
            }}
          />
        )}
      </Popover>
    </>
  );
}

function variantFor(outcome: StageDecisionOutcome): ButtonVariant {
  if (outcome === 'reject') return 'danger';
  if (outcome === 'advance') return 'solid';
  return 'outline';
}

/** What pressing a button does, before it is pressed — there is no undo on an accountable decision. */
function consequence(view: DebriefView): string {
  const next = view.round.nextRoundName
    ? `Advance moves them to ${view.round.nextRoundName} and emails them that they are through.`
    : "Advance marks them passed, ready for final selection on the job's Compare tab; they are not emailed.";
  return `${next} Hold keeps them for 30 days as a silver medalist. Reject sends their regret letter on this job's timing. Any decision locks the round's scorecards and cannot be undone.`;
}
